import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Database, 
  Clock, 
  HardDrive, 
  RefreshCw, 
  Download, 
  ChevronLeft,
  CheckCircle,
  XCircle,
  AlertCircle,
  FileText,
  Calendar,
  Shield
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BackupStatus() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [backupStatus, setBackupStatus] = useState(null);
  const [creatingBackup, setCreatingBackup] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [instructions, setInstructions] = useState('');

  const fetchBackupStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/backup/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch backup status');
      }
      
      const data = await response.json();
      setBackupStatus(data);
    } catch (error) {
      console.error('Error fetching backup status:', error);
      toast.error('Failed to load backup status');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchInstructions = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/backup/restore-instructions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to fetch instructions');
      
      const data = await response.json();
      setInstructions(data.instructions);
    } catch (error) {
      console.error('Error fetching instructions:', error);
    }
  }, [token]);

  useEffect(() => {
    if (user?.role !== 'Admin') {
      toast.error('Admin access required');
      navigate('/');
      return;
    }
    fetchBackupStatus();
    fetchInstructions();
  }, [fetchBackupStatus, fetchInstructions, user, navigate]);

  const handleCreateBackup = async () => {
    setCreatingBackup(true);
    try {
      const response = await fetch(`${API_URL}/api/backup/create`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to create backup');
      
      const result = await response.json();
      toast.success(`Backup created: ${result.total_records} records`);
      fetchBackupStatus();
    } catch (error) {
      console.error('Error creating backup:', error);
      toast.error('Failed to create backup');
    } finally {
      setCreatingBackup(false);
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return 'Never';
    const date = new Date(isoString);
    return date.toLocaleString('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: 'Asia/Kolkata'
    });
  };

  const getTimeSince = (isoString) => {
    if (!isoString) return 'Never';
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    if (diffHours > 0) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return 'Just now';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const latest = backupStatus?.latest_backup;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/')}
                className="gap-2"
              >
                <ChevronLeft className="w-4 h-4" />
                Back
              </Button>
              <div>
                <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                  <Database className="w-5 h-5 text-blue-600" />
                  Backup Status
                </h1>
                <p className="text-sm text-slate-500">MongoDB Atlas automated backups</p>
              </div>
            </div>
            <Button
              onClick={handleCreateBackup}
              disabled={creatingBackup}
              className="gap-2 bg-blue-600 hover:bg-blue-700"
            >
              {creatingBackup ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              {creatingBackup ? 'Creating...' : 'Create Backup Now'}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Status Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Shield className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Status</p>
                  <p className="text-lg font-semibold text-green-600">
                    {backupStatus?.enabled ? 'Active' : 'Disabled'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Clock className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Schedule</p>
                  <p className="text-lg font-semibold text-slate-800">2:00 AM UTC</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Calendar className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Retention</p>
                  <p className="text-lg font-semibold text-slate-800">
                    {backupStatus?.retention_days} days
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <HardDrive className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total Size</p>
                  <p className="text-lg font-semibold text-slate-800">
                    {backupStatus?.total_size_human || '0 B'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Latest Backup Card */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              Latest Backup
            </CardTitle>
            <CardDescription>Most recent backup information</CardDescription>
          </CardHeader>
          <CardContent>
            {latest ? (
              <div className="space-y-4">
                <div className="flex flex-wrap gap-4">
                  <div className="flex-1 min-w-[200px]">
                    <p className="text-sm text-slate-500">Backup Name</p>
                    <p className="font-mono text-sm">{latest.name}</p>
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <p className="text-sm text-slate-500">Date & Time</p>
                    <p className="font-medium">{formatDate(latest.timestamp)}</p>
                    <p className="text-xs text-slate-400">{getTimeSince(latest.timestamp)}</p>
                  </div>
                  <div className="flex-1 min-w-[150px]">
                    <p className="text-sm text-slate-500">Status</p>
                    <Badge 
                      variant={latest.status === 'completed' ? 'default' : 'destructive'}
                      className={latest.status === 'completed' ? 'bg-green-100 text-green-700' : ''}
                    >
                      {latest.status === 'completed' ? (
                        <><CheckCircle className="w-3 h-3 mr-1" /> Completed</>
                      ) : (
                        <><XCircle className="w-3 h-3 mr-1" /> {latest.status}</>
                      )}
                    </Badge>
                  </div>
                  <div className="flex-1 min-w-[100px]">
                    <p className="text-sm text-slate-500">Records</p>
                    <p className="font-medium">{latest.records?.toLocaleString()}</p>
                  </div>
                  <div className="flex-1 min-w-[100px]">
                    <p className="text-sm text-slate-500">Size</p>
                    <p className="font-medium">{latest.size}</p>
                  </div>
                </div>

                {/* Collection breakdown */}
                {latest.collections && (
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm font-medium text-slate-700 mb-2">Collections Backed Up:</p>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(latest.collections).map(([name, count]) => (
                        <Badge key={name} variant="outline" className="text-xs">
                          {name}: {count}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <AlertCircle className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                <p>No backups found</p>
                <p className="text-sm">Create your first backup using the button above</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Backup History */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-slate-600" />
              Backup History
            </CardTitle>
            <CardDescription>
              Last {backupStatus?.backups?.length || 0} backups (max 30 days retained)
            </CardDescription>
          </CardHeader>
          <CardContent>
            {backupStatus?.backups?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-3 font-medium text-slate-600">Backup Name</th>
                      <th className="text-left py-2 px-3 font-medium text-slate-600">Date</th>
                      <th className="text-left py-2 px-3 font-medium text-slate-600">Status</th>
                      <th className="text-right py-2 px-3 font-medium text-slate-600">Records</th>
                      <th className="text-right py-2 px-3 font-medium text-slate-600">Size</th>
                    </tr>
                  </thead>
                  <tbody>
                    {backupStatus.backups.map((backup, index) => (
                      <tr key={backup.backup_name} className={index % 2 === 0 ? 'bg-slate-50' : ''}>
                        <td className="py-2 px-3 font-mono text-xs">{backup.backup_name}</td>
                        <td className="py-2 px-3">{formatDate(backup.timestamp)}</td>
                        <td className="py-2 px-3">
                          {backup.status === 'completed' ? (
                            <span className="text-green-600 flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" /> OK
                            </span>
                          ) : (
                            <span className="text-red-600 flex items-center gap-1">
                              <XCircle className="w-3 h-3" /> {backup.status}
                            </span>
                          )}
                        </td>
                        <td className="py-2 px-3 text-right">{backup.total_records?.toLocaleString()}</td>
                        <td className="py-2 px-3 text-right">{backup.size_human}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-center py-4 text-slate-500">No backup history available</p>
            )}
          </CardContent>
        </Card>

        {/* Restore Instructions */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-slate-600" />
                  Restore Instructions
                </CardTitle>
                <CardDescription>How to restore data from a backup</CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowInstructions(!showInstructions)}
              >
                {showInstructions ? 'Hide' : 'Show'} Instructions
              </Button>
            </div>
          </CardHeader>
          {showInstructions && (
            <CardContent>
              <div className="bg-slate-900 rounded-lg p-4 overflow-x-auto">
                <pre className="text-sm text-slate-100 whitespace-pre-wrap font-mono">
                  {instructions}
                </pre>
              </div>
            </CardContent>
          )}
        </Card>
      </main>
    </div>
  );
}
